import { useEffect, useRef, type ComponentPropsWithoutRef, type JSX, type ReactNode } from 'react';

import { cx } from './internal/cx.js';

export type SnackbarTone = 'info' | 'success' | 'warning' | 'danger';

export interface SnackbarProps extends ComponentPropsWithoutRef<'div'> {
  open: boolean;
  message: ReactNode;
  onClose: () => void;
  tone?: SnackbarTone;
  /** Auto-dismiss after ms. Omit to require manual dismiss. */
  autoHideDuration?: number;
}

const TONE_CLASS: Record<SnackbarTone, string> = {
  info: 'nrx-snackbar--info',
  success: 'nrx-snackbar--success',
  warning: 'nrx-snackbar--warning',
  danger: 'nrx-snackbar--error',
};

/** Transient feedback banner fixed to the viewport bottom. */
export function Snackbar({
  open,
  message,
  onClose,
  tone = 'info',
  autoHideDuration,
  className,
  ...props
}: SnackbarProps): JSX.Element | null {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || autoHideDuration == null) return;
    const timer = window.setTimeout(() => onCloseRef.current(), autoHideDuration);
    return () => window.clearTimeout(timer);
  }, [open, autoHideDuration]);

  if (!open) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cx('nrx-snackbar', TONE_CLASS[tone], className)}
      {...props}
    >
      <span className="nrx-snackbar__message">{message}</span>
      <button type="button" className="nrx-snackbar__close" aria-label="Dismiss" onClick={onClose}>
        ×
      </button>
    </div>
  );
}
